import React from 'react'
import { Helmet } from 'react-helmet-async'

import { METADATA } from '../constants'
import { HREFLANG, Locale, useLocale } from '../context/LocaleContextProvider'
import { useSEOData } from '../context/useSEO'
import { formatArticleStructuredData, formatFaqStructuredData } from '../helpers/formatStructuredData'
import { IMetadata } from '../graphql/sections/metadata'

interface IProps {
  data: IMetadata
}

export const SEO: React.FC<IProps> = ({
  data: {
    title = METADATA.BORROWELL_TITLE,
    author = METADATA.BORROWELL_AUTHOR,
    language = METADATA.BORROWELL_LANGUAGE,
    image,
    keywords: keywordsCopy,
    slug,
    canonicalLink,
    description: descriptionCopy,
  },
}) => {
  const imageApiUrl = image?.image?.file?.url ?? METADATA.BORROWELL_IMAGE
  const imageUrl = imageApiUrl.indexOf('https:') > -1 ? imageApiUrl : `https:${imageApiUrl}`
  const { isFrench, lang, locale, alternates, t } = useLocale()
  const keywords = keywordsCopy?.keywords ?? t('metadata.keywords')
  // French pages are always self-canonical: `canonicalLink` isn't localized, so it holds the English value, and a
  // French page with an English canonical would drop out of the index.
  const englishCanonicalLink = canonicalLink ? canonicalLink : `${METADATA.BORROWELL_URL}${slug || ''}`
  const computedCanonicalLink = isFrench ? `${METADATA.BORROWELL_URL}${alternates[locale] ?? ''}` : englishCanonicalLink
  // Locale metadata only once the page exists in more than one locale, so English-only pages are unchanged.
  const alternateLocales = Object.keys(alternates) as Locale[]
  const isBilingual = alternateLocales.length > 1
  const ogLocale = (pageLocale: Locale) => HREFLANG[pageLocale].replace('-', '_')
  const inLanguage = isBilingual ? HREFLANG[locale] : undefined
  const description = descriptionCopy?.description ?? t('metadata.description')
  // English pages get the site's `en-CA` unless the Metadata entry names another language (the older French-only
  // pages say `fr`). Entries hold `en`, `EN` or `En` for English.
  const htmlLang = isFrench || /^en$/i.test(language) ? lang : language

  const { faqData, articleData } = useSEOData()
  const formattedFaqData = React.useMemo(
    () => faqData && formatFaqStructuredData(faqData, inLanguage),
    [faqData, inLanguage],
  )

  const formattedArticleData = React.useMemo(
    () => articleData && formatArticleStructuredData(articleData, inLanguage),
    [articleData, inLanguage],
  )

  return (
    <Helmet
      defer={false}
      htmlAttributes={{
        lang: htmlLang,
      }}
    >
      <title>{`${title} | ${METADATA.BORROWELL_TITLE}`}</title>
      <meta name="description" content={description} />

      {/* Facebook */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {isBilingual && <meta property="og:locale" content={ogLocale(locale)} />}
      {isBilingual &&
        alternateLocales
          .filter(alternateLocale => alternateLocale !== locale)
          .map(alternateLocale => (
            <meta key={alternateLocale} property="og:locale:alternate" content={ogLocale(alternateLocale)} />
          ))}
      <meta property="og:image" content={imageUrl} />

      {/* Facebook */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:creator" content={author} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta property="twitter:image" content={imageUrl} />

      {/* Keywords */}
      <meta name="keywords" content={keywords} />

      {/* Canonical Links */}
      <link rel="canonical" href={computedCanonicalLink} />

      {/* Language alternates: reciprocal on both versions, English as x-default */}
      {isBilingual &&
        alternateLocales.map(alternateLocale => (
          <link
            key={alternateLocale}
            rel="alternate"
            hrefLang={HREFLANG[alternateLocale]}
            href={`${METADATA.BORROWELL_URL}${alternates[alternateLocale]}`}
          />
        ))}
      {isBilingual && alternates['en-CA'] && (
        <link rel="alternate" hrefLang="x-default" href={`${METADATA.BORROWELL_URL}${alternates['en-CA']}`} />
      )}

      {/* Structured data */}
      {formattedFaqData && <script type="application/ld+json">{formattedFaqData}</script>}
      {formattedArticleData && <script type="application/ld+json">{formattedArticleData}</script>}
    </Helmet>
  )
}
